/** @jsx jsx */
import React, { useEffect, useId, useRef, useState } from 'react';
import { jsx, Box } from 'theme-ui';
import DatePicker from 'react-datepicker';
import { registerLocale, setDefaultLocale } from 'react-datepicker';
import { fi, enGB, sv } from 'date-fns/locale';
import moment from 'moment';
import { CalendarIcon, CaretIcon } from './Icon';
import { DayPicker } from 'react-day-picker';
import * as dayPickerLocale from 'react-day-picker/locale';

import 'react-day-picker/style.css';
import 'react-datepicker/dist/react-datepicker.css';
import { useTranslation } from 'react-i18next';
import Button from './Button';
import i18n from '../localization/i18n';

registerLocale('fi', fi);
registerLocale('en', enGB);
registerLocale('sv', sv);
setDefaultLocale('fi');

const datePickerStyle = (newStyle = false, sxx, datePickerSxx) => ({
  input: {
    minWidth: 0,
    width: 'calc(100%)',
    flex: 1,
    bg: 'transparent',
    color: newStyle ? 'black' : 'white',
    fontSize: 2,
    fontWeight: newStyle ? 'body' : 'bold',
    border: newStyle ? 'solid 2px #CED2D9' : 0,
    borderRadius: newStyle ? 1 : 0,
    margin: newStyle ? '1px' : 0,
    padding: newStyle ? 2 : 0,
    cursor: 'pointer',
    '::placeholder': { color: newStyle ? 'grayLight' : 'white', opacity: 1 },
  },
  'input:hover': newStyle
    ? {
        border: 'solid 2px #9EA6B4',
        margin: newStyle ? '1px' : 0,
      }
    : {},
  '.react-datepicker': {
    fontFamily: 'body',
    fontSize: 1,
    boxShadow: 'dropdown',
    border: 0,
    ...sxx,
  },
  '.react-datepicker__current-month, ': {
    color: 'text',
  },
  '.react-datepicker__header': {
    bg: 'blueLighter',
    border: 0,
  },

  '.react-datepicker-popper[data-placement^="bottom"] .react-datepicker__triangle': {
    borderBottomColor: 'blueLighter',
  },
  '.react-datepicker-popper[data-placement^="bottom"] .react-datepicker__triangle::before': {
    borderBottom: 0,
  },
  '.react-datepicker__day, .react-datepicker__day-name': {
    width: 40,
    lineHeight: '40px',
    margin: '1px',
    color: 'text',
  },
  '.react-datepicker__day--selected': {
    bg: 'primary',
    color: 'white',
  },
  '.react-datepicker__day--disabled': {
    bg: 'white',
    color: '#ccc',
  },
  ...datePickerSxx,
});

// used by PickupTimeForm.js
// sopimusasiakkaan noutotilaus /noutotilaus
// https://testwww.matkahuolto.fi/noutotilaus
export const DateField = ({ field, form, ...props }) => {
  const { i18n } = useTranslation();
  return (
    <Box sx={datePickerStyle(true)}>
      <DatePicker
        selected={field.value}
        onChange={(value) => form.setFieldValue(field.name, value)}
        locale={i18n.language}
        {...props}
      />
    </Box>
  );
};

// used by ebus-ticket-cancellation-form
export const DateInputOld = ({ newStyle, icon = true, ...props }) => {
  const { i18n } = useTranslation();

  return (
    <Box
      sx={{
        display: newStyle ? 'flex' : 'initial',
        alignItems: 'center',
        ...datePickerStyle(newStyle, props?.mainSx, props?.datePickerSxx),
      }}
    >
      {!!newStyle && icon && <CalendarIcon sx={{ mr: 2, flex: 'none' }} />}
      <DatePicker locale={i18n.language} {...props} />
    </Box>
  );
};

// https://daypicker.dev/guides/input-fields#input-with-date-picker-dialog
export function DateInput({ selectedDate, onChange, inputRef }) {
  const { t: translate } = useTranslation();
  const dialogRef = useRef(null);
  const dialogId = useId();
  const headerId = useId();
  const dateId = useId();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const toggleDialog = () => setIsDialogOpen(!isDialogOpen);
  // when dialog is opened, focus selected day not month navigation buttons
  const [monthNavHack, setMonthNavHack] = useState(true);

  // Hook to handle the body scroll behavior and focus trapping
  useEffect(() => {
    const handleBodyScroll = (isOpen) => {
      document.body.style.overflow = isOpen ? 'hidden' : '';
    };
    if (!dialogRef.current) return;
    if (isDialogOpen) {
      handleBodyScroll(true);
      dialogRef.current.showModal();
      setTimeout(() => setMonthNavHack(false), 100);
    } else {
      handleBodyScroll(false);
      dialogRef.current.close();
      setMonthNavHack(true);
    }
    return () => {
      handleBodyScroll(false);
    };
  }, [isDialogOpen]);

  const handleDayPickerSelect = (date) => {
    if (!date) {
      onChange(undefined);
    } else {
      onChange(date);
    }
    dialogRef.current?.close();
  };

  const locale =
    {
      fi: dayPickerLocale.fi,
      sv: dayPickerLocale.sv,
      en: dayPickerLocale.enGB,
    }[i18n.language] || dayPickerLocale.enGB;

  return (
    <div>
      <Button
        ref={inputRef}
        variant="plain"
        sx={{
          display: 'flex',
          flex: 1,
          alignItems: 'center',
          color: 'white',
          borderRadius: 0,
          ':hover': { color: 'white' },
        }}
        onClick={toggleDialog}
        aria-controls="dialog"
        aria-haspopup="dialog"
        aria-expanded={isDialogOpen}
        aria-label={translate('timeTableWidget.ariaToggle')}
        aria-describedby={dateId}
      >
        <CalendarIcon sx={{ mr: 2, flex: 'none' }} />
        <Box id={dateId}>{moment(selectedDate).format('DD.MM.YYYY')}</Box>

        <CaretIcon sx={{ flex: 'none', ml: 1 }} />
      </Button>

      <dialog
        role="dialog"
        ref={dialogRef}
        id={dialogId}
        aria-modal
        aria-labelledby={headerId}
        onClose={() => setIsDialogOpen(false)}
      >
        <DayPicker
          locale={locale}
          autoFocus
          disableNavigation={monthNavHack}
          mode="single"
          selected={selectedDate}
          onSelect={handleDayPickerSelect}
          // footer={selectedDate !== undefined && `Selected: ${selectedDate.toDateString()}`}
          labels={{
            labelPrevious: () => translate('timeTableWidget.ariaPrevMonth'),
            labelNext: () => translate('timeTableWidget.ariaNextMonth'),
          }}
        />
      </dialog>
    </div>
  );
}
